import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { userApi, User } from '../api/user'

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  isAdmin: boolean
  login: (token: string, user: User) => void
  logout: () => void
  checkAuth: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const login = (token: string, userData: User) => {
    localStorage.setItem('user_token', token)
    setUser(userData)
  }

  const logout = () => {
    localStorage.removeItem('user_token')
    setUser(null)
  }

  const checkAuth = async () => {
    const token = localStorage.getItem('user_token')
    if (!token) {
      setIsLoading(false)
      return
    }

    try {
      const response = await userApi.getMe()
      setUser(response.data)
    } catch {
      // Token invalid or expired
      localStorage.removeItem('user_token')
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    checkAuth()
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        isAdmin: user?.email === 'rodolphe.poupardin@gmail.com',
        login,
        logout,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}
